import React, { useMemo, useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { Star, Plus, X, CreditCard, ShoppingBag, QrCode, Copy, Download, RefreshCw } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faArrowLeft, faTimes } from '@fortawesome/free-solid-svg-icons';
import { QRCodeCanvas } from 'qrcode.react';
import qrBillingService from '../../services/qrBillingService';
import RecipeListScreen from './QRitemmap';
import './QRBillingOnboardingComplete.css';

// ==========================================
// TAX MODEL COMPONENTS - INTEGRATED INLINE
// ==========================================

// Simple icon components for Tax Model
const TaxChevronRightIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TaxChevronLeftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TaxShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TaxFileTextIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="10 9 9 9 8 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TaxCalculatorIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="2" width="16" height="20" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="8" y1="6" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="16" y1="10" x2="16" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="10" x2="12" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="8" y1="10" x2="8" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="16" y1="14" x2="16" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="14" x2="12" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="8" y1="14" x2="8" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="16" y1="18" x2="16" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="18" x2="12" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="8" y1="18" x2="8" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TaxCheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TaxInfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="16" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="8" x2="12.01" y2="8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TaxAlertCircleIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TaxChevronDownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TaxCheckCircleIcon = () => (
  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TaxDownloadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="7 10 12 15 17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const getTableSortParts = (table, index = 0) => {
  const name = String(table?.name || '').trim();
  const id = String(table?.id || '').trim();
  const nameMatch = name.match(/(\d+)/);
  const idMatch = id.match(/(\d+)/);
  const num = nameMatch ? parseInt(nameMatch[1], 10) : idMatch ? parseInt(idMatch[1], 10) : Number.POSITIVE_INFINITY;
  const hasNumber = Number.isFinite(num);
  return {
    group: hasNumber ? 0 : 1,
    num: hasNumber ? num : Number.POSITIVE_INFINITY,
    name: name.toLowerCase(),
    index
  };
};

const sortTablesAscending = (tables = []) => {
  const withIndex = tables.map((t, i) => ({ t, i }));
  withIndex.sort((a, b) => {
    const pa = getTableSortParts(a.t, a.i);
    const pb = getTableSortParts(b.t, b.i);
    if (pa.group !== pb.group) return pa.group - pb.group;
    if (pa.num !== pb.num) return pa.num - pb.num;
    const nameCompare = pa.name.localeCompare(pb.name);
    if (nameCompare !== 0) return nameCompare;
    return pa.index - pb.index;
  });
  return withIndex.map((item) => item.t);
};

// Tax Intro Screen Component
const TaxIntroScreen = ({ onStart, onBack }) => {
  return (
    <div className="qrb-complete-tax-intro-screen">
      <div className="qrb-complete-tax-intro-container">
        {onBack && (
          <button onClick={onBack} className="qrb-complete-tax-back-button">
            ←
          </button>
        )}
        
        <div className="qrb-complete-tax-intro-header">
          <h1 className="qrb-complete-tax-intro-title">INVEXIS</h1>
          <p className="qrb-complete-tax-intro-subtitle">QR Dining Experience</p>
        </div>

        <div className="qrb-complete-tax-intro-logo">
          <div className="qrb-complete-tax-logo-circle">
            <div className="qrb-complete-tax-logo-grid">
              <div className="qrb-complete-tax-logo-dot active"></div>
              <div className="qrb-complete-tax-logo-dot active"></div>
              <div className="qrb-complete-tax-logo-dot active"></div>
              <div className="qrb-complete-tax-logo-dot"></div>
            </div>
          </div>
        </div>

        <div className="qrb-complete-tax-intro-title-section">
          <h2 className="qrb-complete-tax-intro-main-title">Tax Setup</h2>
          <p className="qrb-complete-tax-intro-description">Configure GST Filing Status</p>
        </div>

        <div className="qrb-complete-tax-features-list">
          <div className="qrb-complete-tax-feature-item">
            <div className="qrb-complete-tax-feature-icon">
              <TaxShieldIcon />
            </div>
            <div className="qrb-complete-tax-feature-content">
              <p className="qrb-complete-tax-feature-title">Legal Compliance</p>
              <p className="qrb-complete-tax-feature-subtitle">100% GST compliant billing</p>
            </div>
          </div>

          <div className="qrb-complete-tax-feature-item">
            <div className="qrb-complete-tax-feature-icon">
              <TaxFileTextIcon />
            </div>
            <div className="qrb-complete-tax-feature-content">
              <p className="qrb-complete-tax-feature-title">Automated Billing</p>
              <p className="qrb-complete-tax-feature-subtitle">Smart invoice generation</p>
            </div>
          </div>

          <div className="qrb-complete-tax-feature-item">
            <div className="qrb-complete-tax-feature-icon">
              <TaxCalculatorIcon />
            </div>
            <div className="qrb-complete-tax-feature-content">
              <p className="qrb-complete-tax-feature-title">Accurate Tax Splits</p>
              <p className="qrb-complete-tax-feature-subtitle">Precise calculations</p>
            </div>
          </div>
        </div>
      </div>

      <div className="qrb-complete-tax-intro-actions">
        <button onClick={onStart} className="qrb-complete-tax-start-button">
          Get Started
          <TaxChevronRightIcon />
        </button>
        <p className="qrb-complete-tax-completion-time">Takes only 2 minutes to complete</p>
      </div>
    </div>
  );
};

// Tax Scheme Selection Component
const TaxSchemeSelection = ({ onSelect, onBack, selectedScheme }) => {
  const [selectedSchemeId, setSelectedSchemeId] = useState(selectedScheme?.id || null);

  const taxSchemes = [
    {
      id: 'standalone',
      title: 'Standalone Restaurant / QSR',
      rate: 5.0,
      itc: false,
      billFormat: 'Tax Invoice',
      description: '5% GST with no Input Tax Credit eligibility',
    },
    {
      id: 'hotel',
      title: 'Hotel Restaurant',
      rate: 18.0,
      itc: true,
      billFormat: 'Tax Invoice',
      description: '18% GST with full ITC eligibility',
    },
    {
      id: 'composition',
      title: 'Composition Scheme',
      rate: 0.0,
      itc: false,
      billFormat: 'Bill of Supply',
      description: 'Flat turnover tax with no ITC',
    },
    {
      id: 'mixed',
      title: 'Mixed Service / High-Value Catering',
      rate: 18.0,
      itc: true,
      billFormat: 'Tax Invoice',
      description: '18% GST for premium services with full ITC',
    },
  ];

  const handleSelect = (scheme) => {
    setSelectedSchemeId(scheme.id);
    // Call onSelect to notify parent component that a scheme has been selected
    if (onSelect) {
      onSelect(scheme);
    }
  };

  return (
    <div className="qrb-complete-tax-scheme-selection">
      <div className="qrb-complete-tax-scheme-header">
        <div className="qrb-complete-tax-header-top">
          {onBack && (
            <button
              className="back-btn"
              aria-label="Back"
              onClick={onBack}
              style={{
                background: 'transparent',
                border: 'none',
                boxShadow: 'none'
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 22, color: '#fff' }} />
            </button>
          )}
          <h2 className="qrb-complete-tax-scheme-title">Select Tax Scheme</h2>
        </div>

        <div className="qrb-complete-tax-scheme-intro">
          <p className="qrb-complete-tax-scheme-description">
            What is the official GST Filing Status and Service Type for this location?
          </p>
        </div>

        <div className="qrb-complete-tax-info-note">
          <TaxInfoIcon />
          <p className="qrb-complete-tax-info-text">
            This defines the legal tax identity and automatically sets the default rate, ITC status, and bill format.
          </p>
        </div>
      </div>

      <div className="qrb-complete-tax-scheme-list">
        <div className="qrb-complete-tax-scheme-container">
          {taxSchemes.map((scheme) => (
            <button
              key={scheme.id}
              onClick={() => handleSelect(scheme)}
              className={`qrb-complete-tax-scheme-card ${selectedSchemeId === scheme.id ? 'selected' : ''}`}
            >
              {selectedSchemeId === scheme.id && (
                <div className="qrb-complete-tax-check-badge">
                  <TaxCheckIcon />
                </div>
              )}

              <h3 className="qrb-complete-tax-scheme-card-title">{scheme.title}</h3>
              <p className="qrb-complete-tax-scheme-card-description">{scheme.description}</p>

              <div className="qrb-complete-tax-scheme-details">
                <div className="qrb-complete-tax-detail-row">
                  <span className="qrb-complete-tax-detail-label">Default Rate</span>
                  <span className="qrb-complete-tax-detail-value rate">{scheme.rate}%</span>
                </div>

                <div className="qrb-complete-tax-detail-row bordered">
                  <span className="qrb-complete-tax-detail-label">ITC Eligibility</span>
                  <span className={`qrb-complete-tax-detail-value ${scheme.itc ? 'itc-yes' : 'itc-no'}`}>
                    {scheme.itc ? 'Yes' : 'No'}
                  </span>
                </div>

                <div className="qrb-complete-tax-detail-row bordered">
                  <span className="qrb-complete-tax-detail-label">Bill Format</span>
                  <span className="qrb-complete-tax-detail-value">{scheme.billFormat}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Tax Additional Details Component
const TaxAdditionalDetails = ({ onSubmit, scheme, onBack, onChange }) => {
  const [formData, setFormData] = useState({
    alcoholVatRate: '',
    gstin: '',
    stateCode: '',
  });

  const [errors, setErrors] = useState({
    alcoholVatRate: '',
    gstin: '',
    stateCode: '',
  });

  // State codes with default VAT rates for alcohol
  const stateCodes = [
    { code: 'AN', name: 'Andaman and Nicobar Islands', defaultVat: 20.0 },
    { code: 'AP', name: 'Andhra Pradesh', defaultVat: 25.0 },
    { code: 'AR', name: 'Arunachal Pradesh', defaultVat: 20.0 },
    { code: 'AS', name: 'Assam', defaultVat: 25.0 },
    { code: 'BR', name: 'Bihar', defaultVat: 25.0 },
    { code: 'CH', name: 'Chandigarh', defaultVat: 20.0 },
    { code: 'CT', name: 'Chhattisgarh', defaultVat: 25.0 },
    { code: 'DN', name: 'Dadra and Nagar Haveli', defaultVat: 20.0 },
    { code: 'DD', name: 'Daman and Diu', defaultVat: 20.0 },
    { code: 'DL', name: 'Delhi', defaultVat: 20.0 },
    { code: 'GA', name: 'Goa', defaultVat: 20.0 },
    { code: 'GJ', name: 'Gujarat', defaultVat: 0.0 }, // Gujarat has prohibition
    { code: 'HR', name: 'Haryana', defaultVat: 25.0 },
    { code: 'HP', name: 'Himachal Pradesh', defaultVat: 25.0 },
    { code: 'JK', name: 'Jammu and Kashmir', defaultVat: 25.0 },
    { code: 'JH', name: 'Jharkhand', defaultVat: 25.0 },
    { code: 'KA', name: 'Karnataka', defaultVat: 20.0 },
    { code: 'KL', name: 'Kerala', defaultVat: 25.0 },
    { code: 'LA', name: 'Ladakh', defaultVat: 20.0 },
    { code: 'LD', name: 'Lakshadweep', defaultVat: 20.0 },
    { code: 'MP', name: 'Madhya Pradesh', defaultVat: 25.0 },
    { code: 'MH', name: 'Maharashtra', defaultVat: 25.0 },
    { code: 'MN', name: 'Manipur', defaultVat: 0.0 }, // Manipur has prohibition
    { code: 'ML', name: 'Meghalaya', defaultVat: 20.0 },
    { code: 'MZ', name: 'Mizoram', defaultVat: 0.0 }, // Mizoram has prohibition
    { code: 'NL', name: 'Nagaland', defaultVat: 0.0 }, // Nagaland has prohibition
    { code: 'OR', name: 'Odisha', defaultVat: 25.0 },
    { code: 'PY', name: 'Puducherry', defaultVat: 20.0 },
    { code: 'PB', name: 'Punjab', defaultVat: 25.0 },
    { code: 'RJ', name: 'Rajasthan', defaultVat: 25.0 },
    { code: 'SK', name: 'Sikkim', defaultVat: 20.0 },
    { code: 'TN', name: 'Tamil Nadu', defaultVat: 20.0 },
    { code: 'TG', name: 'Telangana', defaultVat: 25.0 },
    { code: 'TR', name: 'Tripura', defaultVat: 20.0 },
    { code: 'UP', name: 'Uttar Pradesh', defaultVat: 25.0 },
    { code: 'UT', name: 'Uttarakhand', defaultVat: 25.0 },
    { code: 'WB', name: 'West Bengal', defaultVat: 25.0 },
  ];

  const handleInputChange = (field, value) => {
    let newFormData = { ...formData, [field]: value };
    
    // When state is selected, automatically populate the default VAT rate
    if (field === 'stateCode' && value) {
      const selectedState = stateCodes.find(state => state.code === value);
      if (selectedState && selectedState.defaultVat !== undefined) {
        newFormData.alcoholVatRate = selectedState.defaultVat.toString();
      }
    }
    
    setFormData(newFormData);
    if (onChange) {
      onChange(newFormData);
    }
  };

  const validateGSTIN = (gstin) => {
    const gstinRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstinRegex.test(gstin);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = {
      alcoholVatRate: '',
      gstin: '',
      stateCode: '',
    };

    if (!formData.alcoholVatRate || parseFloat(formData.alcoholVatRate) < 0 || parseFloat(formData.alcoholVatRate) > 100) {
      newErrors.alcoholVatRate = 'Please enter a valid VAT rate between 0 and 100';
    }

    if (!formData.gstin) {
      newErrors.gstin = 'GSTIN is required';
    } else if (!validateGSTIN(formData.gstin)) {
      newErrors.gstin = 'Please enter a valid GSTIN format';
    }

    if (!formData.stateCode) {
      newErrors.stateCode = 'Please select a state';
    }

    setErrors(newErrors);

    if (!newErrors.alcoholVatRate && !newErrors.gstin && !newErrors.stateCode) {
      onSubmit(formData);
    }
  };

  return (
    <div className="qrb-complete-tax-additional-details">
      <div className="qrb-complete-tax-details-header">
        <div className="qrb-complete-tax-header-top">
          {onBack && (
            <button
              className="back-btn"
              aria-label="Back"
              onClick={onBack}
              style={{
                background: 'transparent',
                border: 'none',
                boxShadow: 'none'
              }}
            >
              <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 16, color: '#fff' }} />
            </button>
          )}
          <h2 className="qrb-complete-tax-details-title">Additional Details</h2>
        </div>

        {scheme && (
          <div className="qrb-complete-tax-selected-scheme">
            <p className="qrb-complete-tax-scheme-label">Selected Scheme</p>
            <p className="qrb-complete-tax-scheme-name">{scheme.title}</p>
            <p className="qrb-complete-tax-scheme-info">
              {scheme.rate}% GST • {scheme.itc ? 'ITC Eligible' : 'No ITC'} • {scheme.billFormat}
            </p>
          </div>
        )}

        <div className="qrb-complete-tax-details-intro">
          <p className="qrb-complete-tax-details-description">
            Complete your tax configuration with essential details
          </p>
        </div>
      </div>

      <div className="qrb-complete-tax-details-form">
        <div className="qrb-complete-tax-form-container">
          <form onSubmit={handleSubmit} className="qrb-complete-tax-form">
            <div className="qrb-complete-tax-form-group">
              <label htmlFor="stateCode" className="qrb-complete-tax-form-label">
                State Code
              </label>
              <div className="qrb-complete-tax-select-wrapper">
                <select
                  id="stateCode"
                  value={formData.stateCode}
                  onChange={(e) => handleInputChange('stateCode', e.target.value)}
                  className={`qrb-complete-tax-form-select ${errors.stateCode ? 'error' : ''}`}
                >
                  <option value="">Select your state</option>
                  {stateCodes.map((state) => (
                    <option key={state.code} value={state.code}>
                      {state.code} - {state.name}
                    </option>
                  ))}
                </select>
                <TaxChevronDownIcon />
              </div>
              {errors.stateCode && (
                <div className="qrb-complete-tax-error-message">
                  <TaxAlertCircleIcon />
                  <span>{errors.stateCode}</span>
                </div>
              )}
              <p className="qrb-complete-tax-form-help"style={{ marginTop: '-0.5rem' }}>
                Required for inter-state GST calculations
              </p>
            </div>

            <div className="qrb-complete-tax-form-group">
              <label htmlFor="alcoholVatRate" className="qrb-complete-tax-form-label">
                State VAT Rate for Alcohol (%)
              </label>
              <input
                type="number"
                id="alcoholVatRate"
                step="0.1"
                max="100"
                value={formData.alcoholVatRate}
                onChange={(e) => handleInputChange('alcoholVatRate', e.target.value)}
                className={`qrb-complete-tax-form-input ${errors.alcoholVatRate ? 'error' : ''}`}
                placeholder="e.g., 20.0"
              />
              {errors.alcoholVatRate && (
                <div className="qrb-complete-tax-error-message">
                  <TaxAlertCircleIcon />
                  <span>{errors.alcoholVatRate}</span>
                </div>
              )}
              <p className="qrb-complete-tax-form-help">
                Handles the dual taxation model for liquor sales
              </p>
            </div>

            <div className="qrb-complete-tax-form-group">
              <label htmlFor="gstin" className="qrb-complete-tax-form-label">
                GSTIN (GST Identification Number)
              </label>
              <input
                type="text"
                id="gstin"
                value={formData.gstin}
                onChange={(e) => handleInputChange('gstin', e.target.value.toUpperCase())}
                className={`qrb-complete-tax-gstin-input ${errors.gstin ? 'error' : ''}`}
                placeholder="e.g., 29ABCDE1234F1Z5"
                maxLength={15}
              />
              {errors.gstin && (
                <div className="qrb-complete-tax-error-message">
                  <TaxAlertCircleIcon />
                  <span>{errors.gstin}</span>
                </div>
              )}
              <p className="qrb-complete-tax-form-help">
                Essential for printing on Tax Invoice and calculating IGST
              </p>
            </div>


          </form>
        </div>
      </div>
    </div>
  );
};

// Tax Completion Screen Component
const TaxCompletionScreen = ({ config, onComplete, onBack, tableCount, businessModel }) => {
  return (
    <div className="qrb-complete-tax-completion-screen">
      <div className="qrb-complete-tax-completion-header">
        <div className="qrb-complete-tax-success-section">
          <div className="qrb-complete-tax-completion-header-top">
            {onBack && (
              <button
                className="back-btn-completion"
                aria-label="Back"
                onClick={onBack}
                style={{
                  background: 'transparent',
                  border: 'none',
                  boxShadow: 'none'
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 24, color: '#fff' }} />
              </button>
            )}
            <h2 className="qrb-complete-tax-completion-title">Setup Complete!</h2>
          </div>
          
          <div className="qrb-complete-tax-success-icon">
            <TaxCheckCircleIcon />
          </div>

          <p className="qrb-complete-tax-success-description">
            Your tax configuration has been successfully saved. Your restaurant is now ready for compliant billing.
          </p>
        </div>
      </div>

      <div className="qrb-complete-tax-completion-content">
        <div className="qrb-complete-tax-content-container">
          <div className="qrb-complete-tax-config-summary">
            <h3 className="qrb-complete-tax-summary-title">
              Configuration Summary
            </h3>

            <div className="qrb-complete-tax-summary-details">
              <div className="qrb-complete-tax-detail-item">
                <span className="qrb-complete-tax-detail-label">Total Tables</span>
                <span className="qrb-complete-tax-detail-value">{tableCount || 0}</span>
              </div>

              <div className="qrb-complete-tax-detail-item bordered">
                <span className="qrb-complete-tax-detail-label">Business Model</span>
                <span className="qrb-complete-tax-detail-value">
                  {businessModel === 'pay-first' ? 'Pay First, Eat Later' : 
                   businessModel === 'eat-first' ? 'Eat First, Pay Later' : 
                   businessModel || 'Not Set'}
                </span>
              </div>

              <div className="qrb-complete-tax-detail-item bordered">
                <span className="qrb-complete-tax-detail-label">Tax Scheme</span>
                <span className="qrb-complete-tax-detail-value scheme-title">{config.scheme?.title}</span>
              </div>

              <div className="qrb-complete-tax-detail-item bordered">
                <span className="qrb-complete-tax-detail-label">Default GST Rate</span>
                <span className="qrb-complete-tax-detail-value rate">{config.scheme?.rate}%</span>
              </div>

              <div className="qrb-complete-tax-detail-item bordered">
                <span className="qrb-complete-tax-detail-label">ITC Eligibility</span>
                <span className={`qrb-complete-tax-detail-value ${config.scheme?.itc ? 'itc-yes' : 'itc-no'}`}>
                  {config.scheme?.itc ? 'Yes' : 'No'}
                </span>
              </div>

              <div className="qrb-complete-tax-detail-item bordered">
                <span className="qrb-complete-tax-detail-label">Bill Format</span>
                <span className="qrb-complete-tax-detail-value">{config.scheme?.billFormat}</span>
              </div>

              <div className="qrb-complete-tax-detail-item bordered">
                <span className="qrb-complete-tax-detail-label">Alcohol VAT Rate</span>
                <span className="qrb-complete-tax-detail-value rate">{config.alcoholVatRate}%</span>
              </div>

              <div className="qrb-complete-tax-detail-item bordered">
                <span className="qrb-complete-tax-detail-label">GSTIN</span>
                <span className="qrb-complete-tax-detail-value gstin">{config.gstin}</span>
              </div>

              <div className="qrb-complete-tax-detail-item">
                <span className="qrb-complete-tax-detail-label">State Code</span>
                <span className="qrb-complete-tax-detail-value">{config.stateCode}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Tax Onboarding Component  
const TaxOnboarding = ({ onComplete, goTo, onBack, initialStep = 1, initialConfig = null, onStepChange = null, tableCount, businessModel }) => {
  const [step, setStep] = useState(initialStep);
  const [config, setConfig] = useState(initialConfig || {
    scheme: null,
    alcoholVatRate: '',
    gstin: '',
    stateCode: '',
  });

  // Notify parent when step changes
  const updateStep = (newStep) => {
    setStep(newStep);
    if (onStepChange) {
      onStepChange(newStep);
    }
  };

  const handleSchemeSelect = (scheme) => {
    setConfig({ ...config, scheme });
    // Don't auto-navigate, let user click Next button
  };

  const handleDetailsChange = (details) => {
    setConfig({ ...config, ...details });
  };

  const handleDetailsSubmit = (details) => {
    setConfig({ ...config, ...details });
    updateStep(3);
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete(config);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      updateStep(step - 1);
    } else {
      // At step 1 or 0, go back to previous page (Business Model)
      if (onBack) {
        onBack();
      } else if (goTo) {
        goTo('dashboard');
      }
    }
  };

  const handleNext = () => {
    if (step === 0) {
      updateStep(1);
    } else if (step === 1 && config.scheme) {
      updateStep(2);
    } else if (step === 2) {
      // Trigger form submission for Additional Details
      const form = document.querySelector('.qrb-complete-tax-form');
      if (form) {
        form.requestSubmit();
      }
    } else if (step === 3) {
      handleComplete();
    }
  };

  const isNextDisabled = () => {
    if (step === 1) return !config.scheme;
    if (step === 2) return !config.gstin || !config.stateCode || !config.alcoholVatRate;
    return false;
  };

  return (
    <div className="qrb-complete-tax-onboarding">
      {step === 0 && <TaxIntroScreen onStart={() => setStep(1)} onBack={handleBack} />}
      {step === 1 && <TaxSchemeSelection onSelect={handleSchemeSelect} onBack={handleBack} selectedScheme={config.scheme} />}
      {step === 2 && <TaxAdditionalDetails onSubmit={handleDetailsSubmit} scheme={config.scheme} onBack={handleBack} onChange={handleDetailsChange} />}
      {step === 3 && <TaxCompletionScreen config={config} onComplete={handleComplete} onBack={handleBack} tableCount={tableCount} businessModel={businessModel} />}
      
      {/* Navigation buttons for all steps except intro */}
      {step > 0 && (
        <div className="qrb-complete-tax-navigation">
          {step < 3 && (
            <button 
              className={`qrb-complete-tax-nav-btn qrb-complete-tax-next-btn ${isNextDisabled() ? 'disabled' : ''}`}
              onClick={handleNext}
              disabled={isNextDisabled()}
              style={{ width: '100%' }}
            >
              Next
            </button>
          )}
          {step === 3 && (
            <button className="qrb-complete-tax-nav-btn qrb-complete-tax-complete-btn" onClick={handleComplete} style={{color:"black", width: '100%'}}>
              Next
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ==========================================
// END TAX MODEL COMPONENTS
// ==========================================

// ==========================================
// TABLE CONFIGURATION STEP COMPONENT
// ==========================================
function TableConfigurationStepComponent({ formData, updateFormData }) {
  const [tableCount, setTableCount] = useState(formData.tableCount || 5);
  const [tables, setTables] = useState(formData.tableConfiguration || []);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editingReasonId, setEditingReasonId] = useState(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const reasonSelectorRef = useRef(null);

  // Predefined best spot reasons
  const bestSpotReasons = [
    'Quiet Table',
    'Group Spot',
    'Best Photo Spot',
    'Fresh Air Spot',
    'Custom Reason'
  ];

  // Close reason selector when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (reasonSelectorRef.current && !reasonSelectorRef.current.contains(event.target)) {
        setEditingReasonId(null);
        setSelectedReason('');
        setCustomReason('');
      }
    }
    if (editingReasonId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [editingReasonId]);

  // Initialize tables on mount if count is set but tables are empty
  useEffect(() => {
    if (tableCount > 0 && tables.length === 0) {
      const initialTables = [];
      for (let i = 0; i < tableCount; i++) {
        initialTables.push({ 
          id: 'table-' + (i + 1), 
          name: 'Table ' + (i + 1), 
          isBestSpot: false, 
          bestSpotReason: '' 
        });
      }
      setTables(initialTables);
      updateFormData({ tableCount: tableCount, tableConfiguration: initialTables });
    }
  }, []);

  const sync = (newTables, newCount) => {
    const orderedTables = sortTablesAscending(newTables);
    setTables(orderedTables);
    setTableCount(newCount);
    updateFormData({ tableCount: newCount, tableConfiguration: orderedTables });
  };

  const sortedTables = useMemo(() => sortTablesAscending(tables), [tables]);

  const updateCount = (n) => {
    if (n < 0) return;
    setTableCount(n);
    if (n > tables.length) {
      const nt = tables.slice();
      for (let i = tables.length; i < n; i++) nt.push({ id: 'table-' + (i + 1), name: 'Table ' + (i + 1), isBestSpot: false, bestSpotReason: '' });
      sync(nt, n);
    } else {
      const nt = tables.slice(0, n);
      sync(nt, n);
    }
  };

  const startEdit = (id, name) => {
    setEditingId(id);
    setEditName(name);
  };

  const saveEdit = () => {
    if (!editingId || !editName.trim()) return;
    const nt = tables.map((t) => (t.id === editingId ? { ...t, name: editName.trim() } : t));
    setEditingId(null);
    setEditName('');
    sync(nt, nt.length);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const toggleBest = (id) => {
    const table = tables.find(t => t.id === id);
    if (table && !table.isBestSpot) {
      setEditingReasonId(id);
      setSelectedReason('');
      setCustomReason('');
    } else {
      const nt = tables.map((t) => (t.id === id ? { ...t, isBestSpot: false, bestSpotReason: '' } : t));
      sync(nt, nt.length);
    }
  };

  const computeNextNumber = () => {
    const used = new Set();
    tables.forEach(t => {
      const m = /^(?:Table\s+)?(\d+)$/.exec(t.name.replace(/^Table\s+/i,'').trim()) ?
        /^(?:Table\s+)?(\d+)$/.exec(t.name.replace(/^Table\s+/i,'').trim()) : null;
      if (m) {
        const n = parseInt(m[1], 10);
        if (!isNaN(n)) used.add(n);
      } else {
        const mid = /^table-(\d+)$/.exec(t.id || '');
        if (mid) {
          const n = parseInt(mid[1], 10); 
          if (!isNaN(n)) used.add(n);
        }
      }
    });
    let n = 1;
    while (used.has(n)) n++;
    return n;
  };

  const addTable = () => {
    const nextNum = computeNextNumber();
    const newId = `table-${nextNum}`;
    const displayName = `Table ${nextNum}`;
    const nt = [...tables, { id: newId, name: displayName, isBestSpot: false, bestSpotReason: '' }];
    sync(nt, nt.length);
  };

  return (
    <div>
      <p className="qrb-complete-center qrb-complete-desc" style={{ marginBottom: '24px' }}>
        Configure your restaurant by setting up tables. Each table will get its own unique QR code.
      </p>

      <div className="qrb-complete-section">
        <div className="qrb-complete-center qrb-complete-section-title" style={{ marginBottom: '15px' }}>Number of Tables</div>
        <div className="qrb-complete-counter" style={{ gap: 8 }}>
          <button style={{fontSize:'1.2rem', fontWeight:'bold'}} className="qrb-complete-btn-round" onClick={() => updateCount(tableCount - 1)}>−</button>
          <input
            type="number"
            className="qrb-complete-input"
            style={{ width: 60, textAlign: 'center', fontSize:'1.5rem', fontWeight:'bold' }}
            value={tableCount === undefined || tableCount === null || tableCount === '' ? '' : tableCount}
            placeholder=""
            onChange={e => {
              const raw = e.target.value;
              if (raw === '' || isNaN(Number(raw))) {
                setTableCount('');
                setTables([]);
                updateFormData({ tableCount: '', tableConfiguration: [] });
                return;
              }
              const val = Math.max(0, parseInt(raw, 10));
              updateCount(val);
            }}
          />
          <button style={{fontSize:'1.2rem', fontWeight:'bold'}} className="qrb-complete-btn-round" onClick={() => updateCount(tableCount + 1)}>+</button>
        </div>
      </div>

      <div className="qrb-complete-section">
        <div className="qrb-complete-row" style={{ justifyContent: 'space-between', marginBottom: '12px' }}>
          <div className="qrb-complete-section-title">Table Configuration</div>
        </div>

        <div className="qrb-complete-list">
          {sortedTables.map((t, i) => (
            <div key={t.id} className="qrb-complete-table-item" style={{margin:'0px 10px 15px', background:'#3a3627'}}>
              <div className="qrb-complete-table-header" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div className="qrb-complete-table-num" style={{ marginRight: 8 }}>{String(i + 1)}</div>
                {editingId === t.id ? (
                  <div style={{ flex: 1 }}>
                    <input
                      className="qrb-complete-input"
                      value={editName}
                      placeholder="Table name"
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                    />
                    <div className="qrb-complete-row" style={{ marginTop: 8 }}>
                      <button className="qrb-complete-btn qrb-complete-btn--success" onClick={saveEdit}>Save</button>
                      <button className="qrb-complete-btn" onClick={cancelEdit}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <div className="qrb-complete-table-name" style={{ 
                      color: '#fff', 
                      fontSize: '14px', 
                      fontWeight: 600,
                      display: 'block',
                      visibility: 'visible',
                      opacity: 1
                    }}>
                      {t.name || `Table ${i + 1}`}
                    </div>
                  </div>
                )}
              </div>
              {editingReasonId === t.id && (
                <div 
                  ref={reasonSelectorRef}
                  className="qrb-complete-best-spot-editor" 
                  style={{ 
                    marginTop: '12px', 
                    padding: '16px', 
                    borderRadius: '8px',
                    background:'Transparent',
                    border: '1px solid #374151'
                  }}
                >
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: '12px' }}>
                    {bestSpotReasons.filter(reason => reason !== 'Custom Reason').map((reason, idx) => (
                      <button 
                        key={idx}
                        className={`qrb-complete-btn ${selectedReason === reason ? 'qrb-complete-btn--primary' : ''}`}
                        onClick={() => {
                          const nt = tables.map((t) => 
                            t.id === editingReasonId 
                              ? { ...t, isBestSpot: true, bestSpotReason: reason } 
                              : t
                          );
                          setEditingReasonId(null);
                          setSelectedReason('');
                          setCustomReason('');
                          sync(nt, nt.length);
                        }}
                        style={{ 
                          padding: '8px 16px',
                          fontSize: '14px',
                          borderRadius: '20px',
                          color: '#BBB49B',
                          border: '1px solid #BBB49B'
                        }}
                      >
                        {reason}
                      </button>
                    ))}
                    <button 
                      className={`qrb-complete-btn`}
                      onClick={() => setSelectedReason('Custom Reason')}
                      style={{ 
                        padding: '8px 16px',
                        fontSize: '14px',
                        borderRadius: '20px',
                        color: '#BBB49B',
                        border: '1px solid #BBB49B'
                      }}
                    >
                      Custom Reason
                    </button>
                  </div>
                  
                  {selectedReason === 'Custom Reason' && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: '12px' }}>
                      <input
                        className="qrb-complete-input"
                        value={customReason}
                        onChange={(e) => setCustomReason(e.target.value)}
                        placeholder="Enter your custom reason..."
                        style={{ flex: 1 }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && customReason.trim()) {
                            const finalReason = customReason.trim();
                            const nt = tables.map((t) => 
                              t.id === editingReasonId 
                                ? { ...t, isBestSpot: true, bestSpotReason: finalReason } 
                                : t
                            );
                            setEditingReasonId(null);
                            setSelectedReason('');
                            setCustomReason('');
                            sync(nt, nt.length);
                          }
                        }}
                      />
                      <button 
                        className="qrb-complete-btn qrb-complete-btn--success"
                        onClick={() => {
                          if (customReason.trim()) {
                            const finalReason = customReason.trim();
                            const nt = tables.map((t) => 
                              t.id === editingReasonId 
                                ? { ...t, isBestSpot: true, bestSpotReason: finalReason } 
                                : t
                            );
                            setEditingReasonId(null);
                            setSelectedReason('');
                            setCustomReason('');
                            sync(nt, nt.length);
                          }
                        }}
                        disabled={!customReason.trim()}
                        style={{ 
                          padding: '8px 12px', 
                          width: '40px', 
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#BBB49B'
                        }}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </button>
                      <button 
                        className="qrb-complete-btn"
                        onClick={() => {
                          setEditingReasonId(null);
                          setSelectedReason('');
                          setCustomReason('');
                        }}
                        style={{ 
                          padding: '8px 12px', 
                          width: '40px', 
                          height: '40px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {t.isBestSpot && editingReasonId !== t.id && (
                <div className="qrb-complete-best-spot">
                  <Star size={16} style={{ display: 'inline', marginRight: '6px' }} />
                  Best Spot - {t.bestSpotReason || 'Premium Location'}
                </div>
              )}
            </div>
          ))}
        </div>

        {tables.length === 0 && (
          <div className="qrb-complete-empty">
            <div>No tables configured</div>
            <div>Use the counter above or add custom tables</div>
          </div>
        )}
      </div>

      <div className="qrb-complete-card">
        <div className="qrb-complete-section-title" style={{ marginBottom: 12 }}>Configuration Summary</div>
        <div className="qrb-complete-row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
          <div>Total Tables</div>
          <div style={{ color: 'var(--qrb-accent)', fontWeight: 600 }}>{String(tables.length)}</div>
        </div>
        <div className="qrb-complete-row" style={{ justifyContent: 'space-between' }}>
          <div>QR Codes to Generate</div>
          <div style={{ color: 'var(--qrb-accent)', fontWeight: 600 }}>{String(tables.length)}</div>
        </div>
      </div>
    </div>
  );
}

// ==========================================
// BUSINESS MODEL STEP COMPONENT
// ==========================================
function BusinessModelStepComponent({ formData, updateFormData, onBack }) {
  const [selected, setSelected] = useState(formData.businessModel || '');
  const models = [
    {id:'pay-first', title:'Pay First, Eat Later', desc:'Customers pay for their order upfront before receiving their food. Great for fast-casual dining and reducing payment friction.'},
    {id:'eat-first', title:'Eat First, Pay Later', desc:'Traditional dining experience where customers order, eat, and then pay at the end. Ideal for full-service restaurants.'}
  ];

  const select = (id) => {
    setSelected(id);
    updateFormData({ businessModel: id });
  };

  return (
    <div>
      <p className="qrb-complete-center" style={{ color: '#BBB49B', lineHeight: '24px', marginBottom: '45px', marginTop: '45px' }}>
        Choose the payment flow that best fits your food outlet. This will determine how customers interact with your QR billing system.
      </p>
      <div style={{gap:'15px'}} className="qrb-complete-list">
        {models.map((m) => (
          <div key={m.id} className={`qrb-complete-model-card${selected === m.id ? ' qrb-complete-active' : ''}`} onClick={() => select(m.id)}>
            <div className="qrb-complete-row" style={{ gap: 10, alignItems: 'center', marginBottom: 8 }}>
              {m.id === 'pay-first' ? 
                <CreditCard size={18} style={{ color: selected === m.id ? '#1a1a1a' : '#D1D5DB' }} /> : 
                <ShoppingBag size={18} style={{ color: selected === m.id ? '#1a1a1a' : '#D1D5DB' }} />
              }
              <div className="qrb-complete-model-title" style={{ margin: 0 }}>{m.title}</div>
            </div>
            <p className="qrb-complete-model-desc">{m.desc}</p>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontWeight: 600, color: selected === m.id ? '#1a1a1a' : '#D1D5DB' }}>Key Benefits:</div>
              <ul style={{ paddingLeft: 0, listStylePosition: 'inside', margin: '5px 0 0 0' }}>
                {(m.id === 'pay-first'
                  ? ['Guaranteed payment', 'Faster table turnover', 'Reduced payment disputes', 'Better cash flow']
                  : ['Traditional dining experience', 'Opportunity for upselling', 'Customer can modify orders', 'Better for groups and special occasions']
                ).map((b, i) => (
                  <li key={i} style={{ color: selected === m.id ? '#1a1a1a' : '#D1D5DB', textAlign: 'left', marginBottom: 6, lineHeight: '1.4', fontSize: '14px' }}>{b}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
      {selected && (
        <div style={{ marginTop: 24 }}>
          <div className="qrb-complete-center" style={{ color: '#D1D5DB', marginBottom: 8 }}>
            You've selected: 
            <span style={{ color: 'var(--qrb-accent)', fontWeight: 600 }}>{models.find((x) => x.id === selected)?.title}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// LOYALTY FEATURE STEP COMPONENT
// ==========================================
function LoyaltyFeatureStepComponent({ formData, updateFormData }) {
  const [enabled, setEnabled] = useState(!!formData.loyaltyEnabled);
  const [points, setPoints] = useState(String(formData.pointsPerRupee || 1));
  const [rewards, setRewards] = useState(formData.rewards || []);
  const [showAdd, setShowAdd] = useState(false);
  const [newReward, setNewReward] = useState({ name: '', pointsCost: '', description: '' });

  const toggle = () => {
    const v = !enabled;
    setEnabled(v);
    updateFormData({ loyaltyEnabled: v, pointsPerRupee: v ? parseFloat(points) || 1 : 0, rewards: v ? rewards : [] });
  };

  const onPoints = (val) => {
    setPoints(val);
    if (enabled) updateFormData({ loyaltyEnabled: true, pointsPerRupee: parseFloat(val) || 1, rewards });
  };

  const add = () => {
    if (!newReward.name || !newReward.pointsCost) {
      alert('Please fill in all reward fields');
      return;
    }
    const r = {
      id: 'reward-' + Date.now(),
      name: newReward.name,
      pointsCost: parseInt(newReward.pointsCost, 10) || 0,
      description: newReward.name,
    };
    const updated = rewards.concat(r);
    setRewards(updated);
    setShowAdd(false);
    setNewReward({ name: '', pointsCost: '', description: '' });
    updateFormData({ loyaltyEnabled: enabled, pointsPerRupee: parseFloat(points) || 1, rewards: updated });
  };

  const remove = (id) => {
    const updated = rewards.filter((r) => r.id !== id);
    setRewards(updated);
    updateFormData({ loyaltyEnabled: enabled, pointsPerRupee: parseFloat(points) || 1, rewards: updated });
  };

  return (
    <div>
      <p className="qrb-complete-center" style={{ color: '#BBB49B', lineHeight: '24px', marginBottom: '24px' }}>
        Set up a loyalty program to encourage repeat customers and increase customer engagement with your food outlet.
      </p>

      <div className="qrb-complete-row" style={{ justifyContent: 'space-between', marginBottom: 32 }}>
        <div>
          <div style={{ fontWeight: 600, marginBottom: 8, textAlign:'left', fontSize:'1.2rem' }}>Enable Loyalty Program</div>
          <div style={{ color: '#BBB49B', fontSize: 14 }}>Reward customers with points for every purchase</div>
        </div>
        <div className={`qrb-complete-toggle${enabled ? ' qrb-complete-active' : ''}`} onClick={toggle}>
          <div className="qrb-complete-knob" />
        </div>
      </div>

      {enabled ? (
        <>
          <div className="qrb-complete-section" style={{marginBottom: 16}}>
            <div style={{ fontWeight: 600, color: '#fff', marginBottom: 8, textAlign:'left', fontSize:'1.3rem' }}>Points Configuration</div>
            <div style={{ color: '#BBB49B', marginBottom: 16, fontSize: 14, textAlign:'left' }}>Set how many points customers earn per rupee spent</div>
            <div className="qrb-complete-row" style={{ alignItems: 'center', background: 'transparent', borderRadius: 8, height:'4rem', marginBottom: 12, border: '1px solid rgb(55, 65, 81)' }}>
              <input
                className="qrb-complete-input"
                style={{ maxWidth: '100vw', fontWeight: 700, fontSize: '1.1rem', color: 'white' }}
                value={points}
                onChange={(e) => onPoints(e.target.value)}
                placeholder="Enter points per ₹ (e.g. 1)"
              />
            </div>
            <div className="center" style={{ color: '#D1D5DB', textAlign:'left' }}>
              {`Example : ₹1 = ${Math.round(1 * (parseFloat(points) || 1))} points`}
            </div>
          </div>

          <div className="qrb-complete-section" style={{marginTop: 32}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize:'1.3rem', marginBottom: 8,textAlign:'left' }}>Rewards</div>
                <div style={{ color: '#BBB49B', fontSize: 14, textAlign:'left' }}>Define what customers can redeem with their points</div>
              </div>
              <button className="qrb-complete-btn" style={{background:'#3A3627', color:"white"}} onClick={() => setShowAdd(true)}>
                <Plus size={16} /> Add Reward
              </button>
            </div>

            {rewards.length === 0 && !showAdd && (
              <div className="qrb-complete-empty">
                <div>No rewards yet</div>
                <div>Add rewards to give customers something to work towards</div>
              </div>
            )}

            {showAdd && (
              <div className="qrb-complete-card" style={{ marginBottom: 16, border:'1px solid #374151', background:'#181711' }}>
                <div style={{ fontWeight: 600, marginBottom: 16, textAlign:'left' }}>Add New Reward</div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <input
                    type="text"
                    className="qrb-complete-reward-input-name"
                    placeholder="Reward name (e.g., Free Coffee)"
                    value={newReward.name}
                    onChange={(e) => setNewReward((o) => ({ ...o, name: e.target.value }))}
                  />
                  <input
                    type="number"
                    className="qrb-complete-reward-input-points"
                    placeholder="Points required"
                    value={newReward.pointsCost}
                    onChange={(e) => setNewReward((o) => ({ ...o, pointsCost: e.target.value }))}
                  />
                </div>
                <div className="qrb-complete-row" style={{ gap: 12, marginTop: 12 }}>
                  <button style={{width:'9rem'}} className="qrb-complete-btn" onClick={() => setShowAdd(false)}>
                    Cancel
                  </button>
                  <button className="qrb-complete-btn" style={{background:'#F9C806', color:'black', width:'9rem'}} onClick={add}>
                    Confirm Reward
                  </button>
                </div>
              </div>
            )}

            {rewards.map((r) => (
              <div key={r.id} className="qrb-complete-card" style={{height:'4rem', position: 'relative', marginBottom: 12, background: '#181711', border:'1px solid rgb(55, 65, 81)' }}>
                <button 
                  onClick={() => remove(r.id)} 
                  style={{ 
                    position: 'absolute', 
                    top: '-1px', 
                    right: '-1px', 
                    background: 'rgba(239, 68, 68, 0.1)', 
                    border: '1px solid rgb(55, 65, 81)',
                    borderRadius: '50%',
                    width: '24px',
                    height: '24px',
                    color: '#BBB49B', 
                    cursor: 'pointer',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s ease',
                    fontSize: '12px'
                  }}
                  title="Remove reward"
                >
                  <X size={12} />
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 90, paddingRight: '30px' }}>
                  <div className="qrb-complete-reward-name">{r.name}</div>
                  <div className="qrb-complete-reward-points">{r.pointsCost + ' points'}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="qrb-complete-empty">
          <div style={{ fontSize: 20, color: '#6B7280' }}>Loyalty Program Disabled</div>
          <div style={{ color: '#BBB49B' }}>
            You can enable and configure the loyalty program later from the admin dashboard
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// QR GENERATION STEP COMPONENT
// ==========================================
function QRGenerationStepComponent({ formData, updateFormData, onModalStateChange, onBack, showStep3Popup = false, onStep3PopupClose }) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratedOnce, setIsGeneratedOnce] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [qrResults, setQrResults] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [displayStep3Popup, setDisplayStep3Popup] = useState(false);

  // Show step 3 completion popup when component mounts if flag is set
  useEffect(() => {
    if (showStep3Popup) {
      setDisplayStep3Popup(true);
      const timer = setTimeout(() => {
        setDisplayStep3Popup(false);
        if (onStep3PopupClose) {
          onStep3PopupClose();
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showStep3Popup, onStep3PopupClose]);

  const extractTableNumber = (t, idx) => {
    // ✅ CRITICAL FIX: Extract the FULL table identifier from table name
    const name = String(t?.name || t?.id || '');
    
    // Method 1: Try to match "Table <anything>" pattern - capture everything after "Table "
    const tableMatch = name.match(/Table\s+(.+)/i);
    if (tableMatch && tableMatch[1]) {
      const extracted = tableMatch[1].trim();
      return extracted; // Returns "T1", "table-2", "Ttable-2", etc.
    }
    
    // Method 2: Try to match any alphanumeric identifier (fallback)
    const alphanumMatch = name.match(/([A-Za-z]+[-_]?\d+)/);
    if (alphanumMatch && alphanumMatch[1]) {
      return alphanumMatch[1];
    }
    
    // Method 3: If name IS the identifier (like "T1", "table-2")
    if (name && name !== 'Table') {
      return name;
    }
    
    // Fallback: use the table prefix from config + index
    const prefix = formData.tablePrefix || 'T';
    const fallback = `${prefix}${idx + 1}`;
    return fallback;
  };

  // Generate QR data with real URLs
  const generateMockQRData = () => {
    const config = (formData.tableConfiguration || []);
    const mockData = config.map((t, idx) => {
      const tableNum = extractTableNumber(t, idx);
      // Use deployed customer menu URL for QR codes
      const baseUrl = process.env.REACT_APP_MENU_FRONTEND_URL || 'https://order.invexis.co.in';
      const businessId = localStorage.getItem('businessId');
      const mockUrl = `${baseUrl}?b=${businessId}&table=${tableNum}`;
      
      return {
        table_number: `Table ${tableNum}`,
        numeric_table: tableNum,
        qr_id: `qr_${tableNum}_${Date.now()}`,
        scan_url: mockUrl,
        anchor_url: mockUrl,
        resolved_anchor_url: mockUrl,
        png: null
      };
    });
    
    setQrResults(mockData);
    setIsGeneratedOnce(true);
    
    // Update form data with mock URLs
    const enriched = config.map((t, idx) => {
      const tableNum = extractTableNumber(t, idx);
      const mockDataItem = mockData.find(q => q.numeric_table === tableNum);
      return { 
        ...t, 
        anchor_url: mockDataItem?.anchor_url,
        resolved_anchor_url: mockDataItem?.resolved_anchor_url,
        qr_id: mockDataItem?.qr_id 
      };
    });
    updateFormData({ tableConfiguration: enriched });
  };

  // Initialize with mock data when component loads
  useEffect(() => {
    if ((formData.tableConfiguration || []).length > 0 && !isGeneratedOnce) {
      generateMockQRData();
    }
  }, [formData.tableConfiguration]);

  // ✅ FIX: Auto-regenerate QR codes if they have the old format (numeric only without prefix)
  useEffect(() => {
    if (qrResults.length > 0) {
      // Check if any QR has the old format (table=1 or table=2 instead of table=T1, table=T2)
      const hasOldFormat = qrResults.some(qr => {
        const url = qr.anchor_url || '';
        // Check if URL has table parameter with just a number (no prefix)
        const match = url.match(/[?&]table=(\d+)(?:&|$)/);
        return match !== null; // If matches numeric-only, it's old format
      });
      
      if (hasOldFormat) {
        generateMockQRData();
      }
    }
  }, [qrResults.length]); // Only check when qrResults changes

  const generate = async (forceAll = false) => {
    if (isGenerating) return;
    setIsGenerating(true);
    
    // Simulate API delay for better UX
    setTimeout(() => {
      generateMockQRData();
      setIsGenerating(false);
    }, 1000);
  };

  const download = async () => {
    if (!qrResults.length) return;
    setDownloading(true);
    
    try {
      for (const qr of qrResults) {
        // Create temporary container offscreen
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '-9999px';
        document.body.appendChild(tempDiv);
        
        // Render QRCodeCanvas temporarily
        const root = ReactDOM.createRoot(tempDiv);
        await new Promise((resolve) => {
          root.render(
            React.createElement(QRCodeCanvas, {
              value: qr.resolved_anchor_url || qr.anchor_url,
              size: 280,
              level: 'H',
              includeMargin: true
            })
          );
          setTimeout(resolve, 100); // Give it time to render
        });
        
        // Get the rendered canvas
        const qrCanvas = tempDiv.querySelector('canvas');
        if (!qrCanvas) {
          console.error('QR canvas not rendered');
          document.body.removeChild(tempDiv);
          continue;
        }
        
        // Create final canvas with table label and QR code
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const size = 400;
        const qrSize = 280;
        const qrX = (size - qrSize) / 2;
        const qrY = 60;
        
        canvas.width = size;
        canvas.height = size;
        
        // White background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);
        
        // Add table label at top
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 24px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(qr.table_number, size/2, 40);
        
        // Draw the QR code
        ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);
        
        // Convert to data URL and download
        const dataURL = canvas.toDataURL('image/png');
        const a = document.createElement('a');
        a.href = dataURL;
        a.download = `QR-${qr.table_number.replace(/\s+/g, '-')}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        
        // Clean up
        root.unmount();
        document.body.removeChild(tempDiv);
        
        await new Promise(r => setTimeout(r, 200));
      }
    } catch (e) { 
      console.error('Download failed', e); 
    } finally { 
      setDownloading(false); 
    }
  };

  const openModal = (t) => { 
    setSelected(t); 
    setModalOpen(true); 
    setCopySuccess(false); 
    if (onModalStateChange) onModalStateChange(true); 
  };
  
  const closeModal = () => { 
    setModalOpen(false); 
    setSelected(null); 
    setCopySuccess(false); 
    if (onModalStateChange) onModalStateChange(false); 
  };

  // Close modal on Escape and prevent accidental background scroll when modal open
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && modalOpen) closeModal(); };
    if (modalOpen) {
      document.addEventListener('keydown', onKey);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  return (
    <div>
      <p className="qrb-complete-center" style={{ color: '#BBB49B', lineHeight: '24px', marginBottom: '24px' }}>
        Review your configuration and generate unique QR codes for each table. Each QR code will be linked to your food outlet.
      </p>

      <div style={{ marginBottom: 24 }}>
        {sortTablesAscending(formData.tableConfiguration || []).map((t, i) => {
          // ✅ FIX: Extract full table identifier (T1, T2, etc.) not just numeric part
          const tableId = extractTableNumber(t, i);
          const backendMatch = qrResults.find(q => {
            // Match using the full table identifier
            return String(q.numeric_table) === String(tableId);
          });
          const hasQr = !!backendMatch;
          return (
            <div key={t.id} className="qrb-complete-table-item" style={{ margin: '0px 10px 15px', background:'#322E21'}}>
              <div className="qrb-complete-table-header">
                <div className="qrb-complete-table-num">{String(i + 1)}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: 600, 
                    textAlign: 'left', 
                    marginLeft: '1.5rem',
                    color: '#fff',
                    fontSize: '14px',
                    display: 'block',
                    visibility: 'visible',
                    opacity: 1
                  }}>
                    {t.name || `Table ${i + 1}`}
                  </div>
                </div>
                <div className="qrb-complete-row">
                  {t.isBestSpot && (
                    <div className="qrb-complete-badge" style={{ gap: 4 }}>
                      <Star size={14} />
                    </div>
                  )}
                  <button className="qrb-complete-btn" disabled={!hasQr} onClick={() => openModal({ ...t, anchor_url: backendMatch?.anchor_url, resolved_anchor_url: backendMatch?.resolved_anchor_url, qr_id: backendMatch?.qr_id, png: backendMatch?.png })}>
                    <QrCode size={16} /> {hasQr ? 'Show QR' : 'Waiting...'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className={`qrb-complete-card ${modalOpen ? 'qrb-complete-disabled' : ''}`} style={{ marginBottom: 24 }}>
        {!isGeneratedOnce ? (
          <>
            <button className="qrb-complete-btn qrb-complete-btn--primary" onClick={() => generate(false)} disabled={isGenerating}>
              {isGenerating ? 'Generating...' : 'Generate QR Codes (Demo)'}
            </button>
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', width: '100%', maxWidth: '500px' }}>
              <button style={{flex: 1, minWidth: 0}} className="qrb-complete-btn" onClick={download} disabled={downloading}>{downloading ? 'Preparing...' : 'Download All PNGs'}</button>
              <button style={{flex: 1, minWidth: 0}} className="qrb-complete-btn" onClick={() => generate(true)} disabled={isGenerating} title="Force regeneration for all tables"><RefreshCw size={14} /> {isGenerating ? 'Working...' : 'Regenerate All'}</button>
            </div>
          </div>
        )}
      </div>

      <div className={`qrb-complete-card ${modalOpen ? 'qrb-complete-disabled' : ''}`}>
        <div className="qrb-complete-row" style={{ gap: 8, marginBottom: 12 }}>
          <div style={{ fontWeight: 600 , fontSize:'1.2rem'}}>Next Steps</div>
        </div>
        <div className="qrb-complete-list">
          {[
            'Download and print the QR codes PDF',
            'Place each QR code on its table',
            'Test the QR codes with your mobile device',
            'Your QR billing system demo is ready!',
          ].map((t, i) => (
            <div key={i} className="qrb-complete-row" style={{ gap: 12, marginBottom: 10 }}>
              <div className="qrb-complete-table-num" style={{ width: 24, height: 24, borderRadius: 12 }}>{String(i + 1)}</div>
              <div style={{ color: '#D1D5DB' }}>{t}</div>
            </div>
          ))}
        </div>
      </div>

      {/* QR Modal */}
      <div className={`qrb-complete-modal${modalOpen ? ' qrb-complete-open' : ''}`} style={{ display: modalOpen ? 'block' : 'none' }}>
        <div className="qrb-complete-modal-backdrop" onClick={closeModal} style={{ display: modalOpen ? 'block' : 'none', position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999 }} />
        <div className="qrb-complete-modal-content" style={{ background: '#181711', zIndex: 1000, position: 'fixed', top: '45vh', left: '50vw',transform: 'translate(-50%, -50%)', maxWidth: '520px', width: '90%', maxHeight: '80vh', overflow: 'auto', padding: '20px', borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }} onClick={(e) => e.stopPropagation()}>
          <div className="qrb-complete-modal-header">
            <div style={{ color: 'white' }} className="qrb-complete-modal-title">{(selected && selected.name) || 'Table QR Code'}</div>
            <button style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'white', fontSize: '20px' }} onClick={closeModal}><FontAwesomeIcon icon={faTimes} /></button>
          </div>
          {selected && selected.anchor_url ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
              <div style={{ width: 220, height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', padding: 8, borderRadius: 4 }}>
                <QRCodeCanvas
                  value={selected.resolved_anchor_url || selected.anchor_url}
                  size={204}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <div style={{ width: '100%', background: 'rgb(24, 23, 17)', color: '#F9FAFB', padding: 12, borderRadius: 8, fontSize: 14 }}>
                <div style={{ textAlign: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ wordBreak: 'break-all' }}>{selected.resolved_anchor_url || selected.anchor_url}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="qrb-complete-btn" onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(selected.resolved_anchor_url || selected.anchor_url);
                    setCopySuccess(true);
                    setTimeout(() => setCopySuccess(false), 2000);
                  } catch (err) {
                    console.error('Failed to copy: ', err);
                    const textArea = document.createElement('textarea');
                    textArea.value = selected.resolved_anchor_url || selected.anchor_url;
                    document.body.appendChild(textArea);
                    textArea.select();
                    try {
                      document.execCommand('copy');
                      setCopySuccess(true);
                      setTimeout(() => setCopySuccess(false), 2000);
                    } catch (fallbackErr) {
                      console.error('Fallback copy failed: ', fallbackErr);
                    }
                    document.body.removeChild(textArea);
                  }
                }}>
                  <Copy size={14} /> {copySuccess ? '✓ Copied!' : 'Copy URL'}
                </button>
                <button className="qrb-complete-btn" onClick={async () => {
                  try {
                    // Get the QR code canvas from the modal
                    const qrCanvas = document.querySelector('.qrb-complete-modal-content canvas');
                    if (!qrCanvas) {
                      console.error('QR canvas not found');
                      return;
                    }
                    
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    const size = 400;
                    const qrSize = 280;
                    const qrX = (size - qrSize) / 2;
                    const qrY = 60;
                    
                    canvas.width = size;
                    canvas.height = size;
                    
                    // White background
                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, size, size);
                    
                    // Title text
                    ctx.fillStyle = '#000000';
                    ctx.font = 'bold 24px Arial';
                    ctx.textAlign = 'center';
                    ctx.fillText(selected.name, size/2, 40);
                    
                    // Draw the QR code
                    ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);
                    
                    // Convert to data URL and download
                    const dataURL = canvas.toDataURL('image/png');
                    const a = document.createElement('a');
                    a.href = dataURL;
                    a.download = `QR-${selected.name.replace(/\s+/g, '-')}.png`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  } catch (e) { console.error('Download error:', e); }
                }}><Download size={14} /> Download</button>
              </div>
            </div>
          ) : (
            <div style={{ padding: 32, textAlign: 'center' }}>QR not ready yet.</div>
          )}
        </div>
      </div>

      {/* Step 3 Completion Popup */}
      {displayStep3Popup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.3s ease-in-out'
        }}>
          <div style={{
            background: '#2c2c2c',
            color: 'white',
            padding: '40px 60px',
            borderRadius: '16px',
            textAlign: 'center',
            fontSize: '14px',
            fontWeight: '600',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
            animation: 'scaleIn 0.3s ease-out'
          }}>
            step 3 out of 5 completed
          </div>
        </div>
      )}
    </div>
  );
}

// ==========================================
// MAIN QR BILLING ONBOARDING COMPONENT
// ==========================================
export default function QRBillingOnboardingComplete({ onComplete, onBack: onBackToPreviousScreen, startFromBeginning = false, isInitialSetup = false }) {
  const totalSteps = 5; // Steps: Tables, Business Model, Tax, Menu Items, QR Generation
  const STORAGE_KEY = 'qr_billing_onboarding_persist_v2';
  const COMPLETION_KEY = 'onboarding_qr_billing_completed';
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    tableCount: 5,
    tableConfiguration: [],
    businessModel: '',
    taxConfiguration: null,
    loyaltyEnabled: false,
    pointsPerRupee: 1,
    rewards: [],
    menuItemsConfigured: false, // New field for ItemMapQR step
  });
  // Tax Model state tracking - Start at step 1 (skip intro screen)
  const [taxStep, setTaxStep] = useState(1);
  const [taxConfig, setTaxConfig] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(isInitialSetup);
  const [showProgressPopup, setShowProgressPopup] = useState(false);
  const [progressMessage, setProgressMessage] = useState('');
  const [showStep3Popup, setShowStep3Popup] = useState(false);
  const saveRef = useRef(null);

  // Auto-hide popup after 1 second
  useEffect(() => {
    if (!showPopup) return;
    const timer = setTimeout(() => {
      setShowPopup(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [showPopup]);

  // Apply a body scoping class so only QR Billing styles take effect while this screen is active
  useEffect(() => {
    try { document.body.classList.add('qrb-only'); } catch (_) {}
    return () => { try { document.body.classList.remove('qrb-only'); } catch (_) {} };
  }, []);

  // Hydrate from backend/Redis on mount
  useEffect(() => {
    const loadConfiguration = async () => {
      if (startFromBeginning || isInitialSetup) {
        // Start fresh for initial setup
        setCurrentStep(1);
        setHydrated(true);
        return;
      }

      try {
        setLoading(true);
        const response = await qrBillingService.getConfiguration();
        
        if (response.success) {
          // ✅ NEW: Check for partial progress FIRST - highest priority
          if (response.partialProgress?.currentStep && response.partialProgress?.formData) {
            const { currentStep: savedStep, formData: savedData } = response.partialProgress;
            
            setFormData(savedData);
            setCurrentStep(savedStep);
            setHydrated(true);
            setLoading(false);
            return;
          }
          
          // Check for completed configuration
          const config = response.data;
          
          if (config && config.tables?.length > 0) {
            // Map backend data to form data
            const mappedFormData = {
              tableCount: config.tables?.length || 0,
              tableConfiguration: config.tables || [],
              businessModel: config.businessModel || '',
              taxConfiguration: config.taxConfiguration || null,
              loyaltyEnabled: config.loyaltyEnabled || false,
              pointsPerRupee: config.pointsPerRupee || 1,
              rewards: config.rewards || []
            };

            setFormData(mappedFormData);
            setCompleted(true);
            // For editing (not initial setup), start from last step (5) to go backwards
            setCurrentStep(isInitialSetup ? 1 : 5);
          } else {
            // No partial progress and no completed config - start fresh
            setCurrentStep(1);
          }
        } else {
          setCurrentStep(1);
        }
      } catch (error) {
        console.error('Failed to load configuration:', error);
        setCurrentStep(1);
      } finally {
        setLoading(false);
        setHydrated(true);
      }
    };

    loadConfiguration();
  }, [startFromBeginning, isInitialSetup]);

  // No localStorage persistence - all data saved to backend/Redis on completion

  const updateFormData = (data) => setFormData((prev) => ({ ...prev, ...data }));

  const onBack = () => {
    setCurrentStep((s) => {
      const prevStep = s - 1;
      // Skip step 4 (itemmap) when going backwards if not initial setup
      if (!isInitialSetup && prevStep === 4) {
        return 3; // Jump from 5 to 3, skipping 4
      }
      return Math.max(1, prevStep);
    });
  };

  const isStepValid = useMemo(() => {
    if (currentStep === 1) return formData.tableCount > 0 && (formData.tableConfiguration || []).length > 0;
    if (currentStep === 2) return !!formData.businessModel;
    if (currentStep === 3) return !!formData.taxConfiguration;
    if (currentStep === 4) return true; // menu items configuration is optional
    return true;
  }, [currentStep, formData]);

  const stepTitle = useMemo(() => {
    switch (currentStep) {
      case 1:
        return "Let's get started with your QR Billing setup";
      case 2:
        return 'Select your Business Model';
      case 3:
        return 'Tax Configuration';
      case 4:
        return 'Menu Items & Recipes';
      default:
        return 'QR Code Generation';
    }
  }, [currentStep]);

  const content = useMemo(() => {
    if (currentStep === 1) return <TableConfigurationStepComponent formData={formData} updateFormData={updateFormData} />;
    if (currentStep === 2) return <BusinessModelStepComponent formData={formData} updateFormData={updateFormData} onBack={onBack} />;
    if (currentStep === 3) {
      // Tax Model step - handle completion by moving to next step
      const handleTaxComplete = (taxConfigData) => {
        setTaxConfig(taxConfigData);
        setTaxStep(3); // Mark as completed
        updateFormData({ taxConfiguration: taxConfigData });
        
        // Set flag to show progress popup on next screen
        setShowProgressPopup(true);
        
        // Navigate immediately
        setCurrentStep(isInitialSetup ? 4 : 5);
      };
      
      const handleTaxBack = () => {
        // If we're on the tax completion screen (step 3) and in initial setup, 
        // allow navigating to Recipe List (step 4) to review/edit recipes
        if (isInitialSetup && taxStep === 3) {
          setCurrentStep(4);
        } else {
          setCurrentStep(2);
        }
      };
      
      // Determine initial step: if we have tax config, start from completion screen, otherwise skip intro and go to scheme selection (step 1)
      const initialStep = taxConfig ? 3 : (taxStep === 0 ? 1 : taxStep);
      
      return <TaxOnboarding 
        onComplete={handleTaxComplete} 
        goTo={null} 
        onBack={handleTaxBack}
        initialStep={initialStep}
        initialConfig={taxConfig}
        onStepChange={setTaxStep}
        tableCount={formData.tableCount}
        businessModel={formData.businessModel}
      />;
    }
    if (currentStep === 4 && isInitialSetup) {
      // RecipeListScreen step - Menu items and recipes configuration (only during initial setup)
      return <RecipeListScreen 
        goTo={(step) => {
          if (step === 'map3') {
            // Back button clicked - navigate to Setup Complete (Tax completion) page
            setCurrentStep(3);
            setTaxStep(3); // Ensure we're on the completion screen of tax onboarding
          } else {
            // Continue to next step
            updateFormData({ menuItemsConfigured: true });
            setCurrentStep(5);
          }
        }}
        previousScreen="qr-onboarding"
        showProgressPopup={showProgressPopup}
        onProgressPopupClose={() => setShowProgressPopup(false)}
        onStep3Complete={() => setShowStep3Popup(true)}
      />;
    }
    return <QRGenerationStepComponent formData={formData} updateFormData={updateFormData} onModalStateChange={setModalOpen} onBack={onBack} showStep3Popup={showStep3Popup} onStep3PopupClose={() => setShowStep3Popup(false)} />;
  }, [currentStep, formData, taxConfig, taxStep]);

  const onNext = async () => {
    if (!isStepValid) return;
    
    // ✅ NEW: Save partial progress before moving to next step (except for final step)
    if (currentStep < totalSteps) {
      try {
        await qrBillingService.savePartialProgress({
          currentStep: currentStep,
          formData: formData
        });
      } catch (error) {
        console.error('⚠️ Failed to save partial progress:', error);
        // Continue anyway - don't block user from proceeding
      }
    }
    
    if (currentStep === totalSteps) {
      // Check if this is an edit/update scenario (completed = true)
      if (completed) {
        // Edit mode: Save changes and wait for confirmation
        try {
          setLoading(true);
          const token = localStorage.getItem('token');
          if (!token) {
            throw new Error('No authentication token found');
          }

          // Save complete configuration INCLUDING tables in a single call
          const configPayload = {
            tableConfiguration: formData.tableConfiguration || [],
            businessModel: formData.businessModel,
            taxConfiguration: formData.taxConfiguration,
            loyaltyEnabled: formData.loyaltyEnabled || false,
            pointsPerRupee: formData.pointsPerRupee || 1,
            rewards: formData.rewards || []
          };

          const response = await qrBillingService.saveConfiguration(configPayload);
          
          if (response.success) {
            
            // Clear partial progress data
            await qrBillingService.clearPartialProgress();
            
            // Show success and navigate back
            alert('Configuration updated successfully!');
            if (typeof onComplete === 'function') {
              onComplete();
            }
          } else {
            console.error('❌ Failed to update configuration:', response.error || 'Unknown error');
            alert('Failed to save changes: ' + (response.error || 'Unknown error'));
          }
        } catch (error) {
          console.error('❌ Failed to update configuration:', error);
          alert('Failed to save changes: ' + error.message);
        } finally {
          setLoading(false);
        }
        return;
      }
      
      // Initial setup mode: Save to backend and navigate immediately
      // Call onComplete immediately to navigate to next screen
      if (typeof onComplete === 'function') {
        onComplete();
      }
      
      // Save configuration in background
      (async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            throw new Error('No authentication token found');
          }

          // Save complete configuration INCLUDING tables in a single call
          const configPayload = {
            tableConfiguration: formData.tableConfiguration || [],
            businessModel: formData.businessModel,
            taxConfiguration: formData.taxConfiguration,
            loyaltyEnabled: formData.loyaltyEnabled || false,
            pointsPerRupee: formData.pointsPerRupee || 1,
            rewards: formData.rewards || []
          };

          
          const response = await qrBillingService.saveConfiguration(configPayload);
          
          if (response.success) {
            
            // Clear partial progress data after successful completion
            await qrBillingService.clearPartialProgress();
            
            setCompleted(true);
          } else {
            console.error('❌ Failed to save configuration:', response.error || 'Unknown error');
          }
        } catch (error) {
          console.error('❌ Failed to save configuration:', error);
        }
      })();
      
      return;
    }
    
    // Move to next step
    setCurrentStep(s => s + 1);
  };

  if (!hydrated || loading) {
    return (
      <div style={{padding: 40, color: '#fff', textAlign: 'center'}}>
        <div>{loading ? 'Saving configuration...' : 'Loading...'}</div>
      </div>
    );
  }

  const allowJump = completed;

  return (
    <div className="qrb-complete-onboarding-root">
      {/* Hide header for tax model step and itemmap step as they have their own layout */}
      {currentStep !== 3 && currentStep !== 4 && (
        <div className="qrb-complete-header">
          <div className="qrb-complete-container" style={{ position: 'relative' }}>
            {(currentStep === 1 || currentStep === 2 || currentStep === 5) && (
              <button
                className="back-btn"
                aria-label="Back"
                onClick={() => {
                  if (currentStep === 1 && onBackToPreviousScreen) {
                    // Navigate back to OnboardingPage (pic 1)
                    onBackToPreviousScreen();
                  } else {
                    // Navigate to previous step within QR onboarding
                    onBack();
                  }
                }}
                style={{
                  position: 'absolute',
                  left: '0px',
                  top: '30%',
                  transform: 'translateY(-59%)',
                  background: 'transparent',
                  border: 'none',
                  boxShadow: 'none',
                  marginRight: '2px'
                }}
              >
                <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 16, color: '#fff' }} />
              </button>
            )}
            <div className="qrb-complete-logo">{currentStep === 2 ? stepTitle : 'INVEXIS'}</div>
          </div>
        </div>
      )}
      {/* Full-width content for itemmap (step 4), containerized for others */}
      {currentStep === 4 ? (
        <div className="qrb-complete-content" style={{ padding: 0, paddingBottom: 0 }}>
          {content}
        </div>
      ) : currentStep === 3 ? (
        /* Tax model has its own layout */
        <div className="qrb-complete-content">
          {content}
        </div>
      ) : (
        /* Normal steps with container and title */
        <div className="qrb-complete-container qrb-complete-content">
          {currentStep !== 2 && <h2 className="qrb-complete-title">{stepTitle}</h2>}
          {content}
        </div>
      )}
      {/* Hide navigation for tax model step (step 3) and itemmap (step 4) as they handle their own navigation */}
      {currentStep !== 3 && currentStep !== 4 && (
        <div className={`qrb-complete-nav ${modalOpen || loading ? 'qrb-complete-disabled' : ''}`}>
            <button
              style={{ width: '100%' }}
              className={`qrb-complete-btn qrb-complete-btn--primary qrb-complete-flex-1${!isStepValid || loading ? ' disabled' : ''}`}
              onClick={onNext}
              disabled={!isStepValid || loading}
            >
              {loading ? 'Saving...' : (currentStep === totalSteps ? (completed ? 'Save Changes' : 'Complete Setup') : 'Next')}
            </button>
        </div>
      )}

      {/* Success Popup */}
      {showPopup && (
        <div className="qrb-success-popup-overlay">
          <div className="qrb-success-popup">
            <p className="qrb-success-popup-text">step 1 out of 5 completed</p>
          </div>
        </div>
      )}
    </div>
  );
}